/**
 * inspectionSyncEngine.js — drives offline Monthly Inspection Report drafts to the
 * server when connectivity returns.
 *
 * Per draft, the sync is "photos first, then submit":
 *   1. Upload each buffered photo Blob to S3 (presigned URL fetched at sync time,
 *      so a multi-day offline window never hits the 1-hour expiry). Each photo's
 *      S3 key is persisted as it lands, so an interrupted sync resumes instead of
 *      re-uploading.
 *   2. Once every photo has a key, POST the form payload (with the keys
 *      substituted in) to the existing create endpoint, tagged with clientUuid
 *      for server-side de-duplication.
 *
 * Retry policy (per product decision):
 *   - 4xx except 401  -> permanent: stop auto-retry, flag for the user.
 *   - 401             -> auth: keep the queue intact, stop this run (resumes after
 *                        re-login on next boot/online event).
 *   - 5xx / network / timeout -> transient: retry up to MAX_SYNC_ATTEMPTS, then
 *                        hold for manual retry.
 */

import { toast } from "react-toastify";
import api from "../api";
import s3Service from "../services/s3Service";
import propertyMonthlyInspectionsService from "../services/propertyMonthlyInspectionsService";
import initialInspectionReportsService from "../services/initialInspectionReportsService";
import {
  OFFLINE_PHOTO_PREFIX,
  saveDraft,
  deleteDraft,
  getDraft,
  getSyncableDrafts,
} from "./offlineDb";

// Bump this whenever the engine changes. If you don't see this exact line in the
// browser console on page load, the dev server is serving a STALE bundle and the
// offline fixes are NOT active — restart `npm start` (a refresh isn't enough).
const ENGINE_VERSION = "offline-engine v8 (single-submit + dedup + cross-tab lock)";
console.log(`[gg-offline] ${ENGINE_VERSION} loaded`);

const CHANGE_EVENT = "gg-offline-change";

/** Notify any UI (e.g. PendingSyncBadge) that queue state changed. */
function emitChange() {
  try {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch (_) {
    /* no-op */
  }
}

/** Non-retryable error marker. */
class PermanentError extends Error {
  constructor(message) {
    super(message);
    this.permanent = true;
  }
}

function classifyError(err) {
  if (err && err.permanent) return "permanent";
  const status = err && err.response && err.response.status;
  if (status === 401) return "auth";
  if (status && status >= 400 && status < 500) return "permanent";
  return "transient"; // 5xx, network failure, fetch TypeError, timeout
}

function readableError(err) {
  return (
    (err &&
      err.response &&
      err.response.data &&
      (err.response.data.message ||
        (err.response.data.errors &&
          JSON.stringify(err.response.data.errors)))) ||
    (err && err.message) ||
    "Unknown error"
  );
}

/**
 * Upload every buffered file for a draft (photo slots AND nested checklist
 * files), persisting each S3 key as it lands so an interrupted sync resumes.
 * Returns a placeholder→key map for substitution into the payload.
 *
 * Each buffered record carries its own `folder`, so photos land under
 * `…/reports/mir/` and checklist files under `monthly-inspection-files/…`.
 */
async function uploadDraftFiles(draft) {
  const files = draft.photos || {};
  const map = {}; // placeholder string -> S3 key
  for (const key of Object.keys(files)) {
    const rec = files[key];
    if (!rec) continue;
    const placeholder = OFFLINE_PHOTO_PREFIX + key;
    // Some screens store the full S3 key in the field; others (initial
    // inspections) store just the filename. `rec.filenameOnly` selects which.
    const toValue = (fullKey) =>
      rec.filenameOnly ? fullKey.split("/").pop() : fullKey;

    if (rec.s3Key) {
      map[placeholder] = toValue(rec.s3Key); // uploaded on a prior attempt
      continue;
    }
    if (!rec.blob) {
      // Buffered file with no data — unrecoverable, don't POST a bad string.
      throw new PermanentError(
        `A buffered file ("${rec.fileName || key}") is missing on this device`
      );
    }

    const folder =
      rec.folder ||
      `uploads/images/properties/${draft.propertyId}/reports/mir/`;
    const file = new File([rec.blob], rec.fileName || key, {
      type: rec.mimeType || "application/octet-stream",
    });
    const s3Key = await s3Service.uploadFileToS3(file, folder);
    rec.s3Key = s3Key;
    map[placeholder] = toValue(s3Key);
    // Persist progress so a drop mid-batch resumes from here.
    await saveDraft(draft);
  }
  return map;
}

/**
 * Recursively swap offline placeholders for their real S3 keys throughout the
 * payload — handles flat photo fields and the nested checklist objects alike.
 * Any placeholder with no mapping is nulled out so a bad string is never POSTed.
 */
function applyReplacements(value, map) {
  if (typeof value === "string") {
    if (value.startsWith(OFFLINE_PHOTO_PREFIX)) {
      return map[value] !== undefined ? map[value] : null;
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((v) => applyReplacements(v, map));
  }
  if (value && typeof value === "object") {
    const out = {};
    for (const k of Object.keys(value)) {
      out[k] = applyReplacements(value[k], map);
    }
    return out;
  }
  return value;
}

async function submitDraft(draft, replacementMap) {
  let submitData = { ...draft.formData };

  // Merge rotations the same way the online submit does.
  Object.entries(draft.photoRotations || {}).forEach(([field, degrees]) => {
    if (degrees) submitData[`${field}_rotate`] = degrees;
  });

  // Substitute every buffered placeholder (flat + nested) with its S3 key,
  // and null out any unresolved placeholder.
  submitData = applyReplacements(submitData, replacementMap);

  const isUpdate = draft.mode === "update" && draft.reportId;
  const initial = draft.reportType === "initial";

  if (isUpdate) {
    // Editing an existing report — PUT. Naturally idempotent, so a retried
    // submit re-applies the same values rather than creating a duplicate.
    if (initial) {
      await initialInspectionReportsService.updateInitialInspectionReport(
        draft.reportId,
        submitData
      );
    } else {
      await propertyMonthlyInspectionsService.updateMonthlyInspectionReport(
        draft.reportId,
        submitData
      );
    }
  } else {
    // Creating a new report — POST, tagged for server-side de-dup.
    submitData.client_uuid = draft.clientUuid;
    if (initial) {
      await initialInspectionReportsService.createInitialInspectionReport(
        draft.propertyId,
        submitData
      );
    } else {
      await propertyMonthlyInspectionsService.createMonthlyInspectionReport(
        draft.propertyId,
        submitData
      );
    }
  }
}

/**
 * Property Photos screen: uploads go through the backend as multipart FormData
 * (not S3-presign + keys), so the offline replay re-sends the buffered Blobs as
 * files to the same two endpoints the online Save uses.
 */
async function submitPropertyPhotos(draft) {
  if (draft.mainPhoto && draft.mainPhoto.blob) {
    const fd = new FormData();
    fd.append(
      "main_photo",
      new File([draft.mainPhoto.blob], draft.mainPhoto.fileName || "main.jpg", {
        type: draft.mainPhoto.mimeType || "image/jpeg",
      })
    );
    fd.append("property_id", draft.propertyId);
    await api.put(`/properties/${draft.propertyId}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  const photos = (draft.photos || []).filter((p) => p && p.blob);
  if (photos.length) {
    const fd = new FormData();
    photos.forEach((p) =>
      fd.append(
        "photos",
        new File([p.blob], p.fileName || "photo.jpg", {
          type: p.mimeType || "image/jpeg",
        })
      )
    );
    // Idempotency key so an ambiguous-success retry doesn't duplicate the batch.
    if (draft.clientUuid) fd.append("client_uuid", draft.clientUuid);
    await api.put(`/properties/${draft.propertyId}/photos`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }
}

/**
 * Ticket "Add document": presign-upload each buffered file (done by
 * uploadDraftFiles, which sets rec.s3Key), then one JSON POST per document to
 * the same endpoint the online screen uses. `posted` guards against re-POSTing
 * an already-saved document on a mid-batch retry.
 */
async function submitTicketDocuments(draft) {
  const keys = Object.keys(draft.photos || {});
  console.log(
    `[sync] submitTicketDocuments draft=${draft.id} files=${keys.length}`
  );
  for (const key of keys) {
    const rec = draft.photos[key];
    if (!rec || rec.posted) continue;
    await api.post(`/tickets/${draft.ticketId}/documents`, {
      name: draft.name || rec.fileName,
      key: rec.s3Key,
      file_name: rec.fileName,
      file_type: rec.mimeType,
      file_size: rec.size,
      folder: "documents",
      client_uuid: rec.clientUuid,
    });
    // Remove the file from the draft as soon as it's posted so it can never be
    // re-sent (even if this draft is somehow reprocessed).
    delete draft.photos[key];
    await saveDraft(draft); // persist progress
  }
}

/**
 * Ticket "Create note": presign-upload any buffered image attachments, then a
 * single JSON POST creating the note + attachments (matches the online flow).
 */
async function submitTicketNote(draft) {
  const recs = Object.values(draft.photos || {});
  const attachments = recs
    .filter((r) => r && r.s3Key)
    .map((r) => ({
      name: r.fileName,
      s3Key: r.s3Key,
      size: r.size,
      type: r.mimeType,
    }));
  await api.post(`/ticketsnotes`, {
    description: draft.description,
    ticket_id: draft.ticketId,
    attachments: attachments.length ? attachments : undefined,
    client_uuid: draft.clientUuid,
  });
}

/**
 * Ticket create/edit forms (no file uploads on edit; optional attachments on
 * create). Create attachments are presign-uploaded by uploadDraftFiles first.
 *   - create -> POST /tickets (JSON body + attachments[] + client_uuid)
 *   - update -> PUT /tickets/:id (JSON body; idempotent, no client_uuid needed)
 */
async function submitTicketForm(draft) {
  if (draft.mode === "update" && draft.ticketId) {
    await api.put(`/tickets/${draft.ticketId}`, draft.formData || {});
    return;
  }
  const recs = Object.values(draft.photos || {});
  const attachments = recs
    .filter((r) => r && r.s3Key)
    .map((r) => ({
      name: r.fileName,
      s3Key: r.s3Key,
      size: r.size,
      type: r.mimeType,
    }));
  await api.post(`/tickets`, {
    ...(draft.formData || {}),
    attachments: attachments.length ? attachments : undefined,
    client_uuid: draft.clientUuid,
  });
}

async function processDraft(rawDraft) {
  // Re-read the latest copy in case it changed since the run started.
  const draft = (await getDraft(rawDraft.id)) || rawDraft;
  console.log(
    `[sync] processDraft id=${draft.id} type=${draft.reportType} mode=${draft.mode || "-"} attempts=${draft.attempts || 0} photos=${Object.keys(draft.photos || {}).length}`
  );
  draft.status = "syncing";
  draft.lastError = null;
  await saveDraft(draft);
  emitChange();

  try {
    // If this draft's submit already happened on a previous pass but the draft
    // wasn't deleted (e.g. a delete failed, or it was reprocessed), DO NOT submit
    // it again — just clean it up. This is what prevents duplicate notes/tickets.
    if (draft.submitted) {
      console.log(`[sync] draft ${draft.id} already submitted — cleaning up`);
      await deleteDraft(draft.id);
      emitChange();
      return { ok: true };
    }

    if (draft.reportType === "propertyPhotos") {
      await submitPropertyPhotos(draft);
    } else if (draft.reportType === "ticketDocument") {
      await uploadDraftFiles(draft); // presign + S3 upload, sets rec.s3Key
      await submitTicketDocuments(draft);
    } else if (draft.reportType === "ticketNote") {
      await uploadDraftFiles(draft);
      await submitTicketNote(draft);
    } else if (draft.reportType === "ticketForm") {
      if (draft.mode !== "update") await uploadDraftFiles(draft); // create: upload attachments
      await submitTicketForm(draft);
    } else {
      const replacementMap = await uploadDraftFiles(draft);
      await submitDraft(draft, replacementMap);
    }
    // Mark submitted + persist BEFORE deleting, so if the delete fails the draft
    // is cleaned up on the next pass rather than re-submitted (no duplicates).
    draft.submitted = true;
    await saveDraft(draft);
    await deleteDraft(draft.id);
    console.log(`[sync] draft ${draft.id} synced + deleted`);
    emitChange();
    return { ok: true };
  } catch (err) {
    const kind = classifyError(err);
    draft.attempts = (draft.attempts || 0) + 1;
    draft.lastError = readableError(err);
    draft.status = "failed";
    // A non-idempotent submission (a CREATE) may have already been processed by
    // the server even though the response failed/was lost — auto-retrying it is
    // what produced duplicate tickets. So creates get a single automatic attempt
    // and then wait for an explicit manual retry (which the server-side
    // client_uuid dedup makes safe). Updates (PUT) are idempotent → keep retrying.
    const isIdempotent = draft.mode === "update";
    draft.permanent =
      kind === "permanent" || (!isIdempotent && kind !== "auth");
    await saveDraft(draft);
    emitChange();
    return { ok: false, kind, error: draft.lastError };
  }
}

let isSyncing = false;

// The actual sync pass. Only ever entered while holding the cross-tab lock (or
// the same-tab guard if the Web Locks API is unavailable).
async function runSyncPass() {
  isSyncing = true;
  let synced = 0;
  let failed = 0;
  let needsAttention = false;
  try {
    const drafts = await getSyncableDrafts();
    console.log(`[sync] run — ${drafts.length} syncable draft(s)`);
    for (const d of drafts) {
      if (typeof navigator !== "undefined" && !navigator.onLine) break;
      const res = await processDraft(d);
      if (res.ok) synced += 1;
      else {
        failed += 1;
        if (res.kind === "permanent") needsAttention = true;
      }
    }
  } catch (e) {
    console.warn("[inspectionSync] run error:", e && e.message);
  } finally {
    isSyncing = false;
    emitChange();
  }

  if (synced > 0) {
    toast.success(
      `Offline upload complete — ${synced} item${synced > 1 ? "s" : ""} synced.`
    );
  }
  if (needsAttention) {
    toast.error(
      "A queued report needs attention before it can be uploaded. Open “Pending uploads” to review."
    );
  }

  return { synced, failed, needsAttention };
}

/**
 * Process all syncable drafts. Safe to call often (online event, app boot,
 * manual retry, after queueing).
 *
 * CRITICAL: the offline queue (IndexedDB) is SHARED across browser tabs, but each
 * tab runs its own copy of this engine. Without coordination, every open tab
 * would process the same drafts and create one duplicate per tab. We therefore
 * run the sync pass under a cross-tab Web Lock — only the tab that holds the lock
 * syncs; the others skip. `isSyncing` still guards re-entry within a single tab.
 */
export async function triggerSync() {
  if (isSyncing) return { skipped: true };
  // Refresh any listening UI (e.g. the pending badge) up-front, so a draft just
  // queued while offline is reflected even though we can't sync it yet.
  emitChange();
  if (typeof navigator !== "undefined" && !navigator.onLine)
    return { skipped: true };
  if (!localStorage.getItem("token")) return { skipped: true }; // logged out — keep queue

  // Cross-tab exclusion: if another tab is already syncing, skip in this tab.
  if (typeof navigator !== "undefined" && navigator.locks && navigator.locks.request) {
    return navigator.locks.request(
      "gg-offline-sync",
      { ifAvailable: true },
      async (lock) => {
        if (!lock) {
          console.log("[sync] another tab holds the sync lock — skipping");
          return { skipped: true };
        }
        return runSyncPass();
      }
    );
  }

  // Fallback (no Web Locks API): same-tab guard only.
  return runSyncPass();
}

/** Force a held/permanent draft back into the queue and re-run. */
export async function retryDraft(id) {
  const draft = await getDraft(id);
  if (!draft) return;
  draft.status = "queued";
  draft.permanent = false;
  draft.attempts = 0;
  draft.lastError = null;
  await saveDraft(draft);
  emitChange();
  return triggerSync();
}

export { CHANGE_EVENT };
